import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Trash2, Users, Mail, Bell, Send, FileText,
  Check, X, Calendar, MapPin, ChevronLeft, ChevronRight, Eye,
  Search, Loader2, CheckCircle, UserPlus, Megaphone, MoreHorizontal,
  Paperclip, Download, Upload, ExternalLink, Shield, ArrowRight, ListOrdered
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import {
  getRepEvent, getRepEventInvitations, getRepEventAssociates, getRepEventAssociateProvince,
  getRepEventCaricheOptions, getRepEventInvitationsPreview,
  uploadRepEventAllegato, deleteRepEventAllegato,
  generateRepEventInvitations, sendRepEventInvitations,
  sendRepEventReminder, updateRepEventStato, deleteRepEvent,
  toggleRepEventCheckin, removeRepresentativeToken,
  getRepEventVerbale,
  getEventLifecycleAction
} from '../../services/representativeService';
import { scaricaPdfVerbale } from '../../services/verbaliService';
import { haTestoVisibile, urlAllegato } from '../../components/verbali/VerbaleLettura';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import ConfirmDialog from '../../components/associative/AssocConfirmDialog';

// Messaggio d'errore del download del PDF: con responseType 'blob' anche il
// corpo dell'errore arriva come Blob, quindi va letto prima di cercare `error`.
const messaggioErrorePdf = async (err: any): Promise<string> => {
  const dati = err?.response?.data;
  try {
    if (dati && typeof dati.text === 'function') {
      const json = JSON.parse(await dati.text());
      if (typeof json?.error === 'string' && json.error) return json.error;
    } else if (typeof dati?.error === 'string' && dati.error) {
      return dati.error;
    }
  } catch { /* corpo non JSON */ }
  return 'Errore nel download del PDF';
};

// -- Types --

interface EventType {
  _id: string;
  nome: string;
  colore: string;
}

interface AuthorRep {
  nome: string;
  cognome: string;
}

interface EventData {
  _id: string;
  nome: string;
  tipologiaId: EventType | null;
  dataEvento: string;
  oraInizio?: string;
  oraFine?: string;
  luogo?: string;
  indirizzo?: string;
  linkMappa?: string;
  descrizione?: string;
  /** Punti dell'ordine del giorno; assente negli eventi di prima, [] = nessun ordine del giorno. */
  ordineDelGiorno?: string[];
  emailContent?: string;
  stato: string;
  enableQrCode?: boolean;
  allegatiInvito?: { _id: string; filename: string; originalName: string; mimetype: string; size: number }[];
  isOwner: boolean;
  authorRepresentativeId: AuthorRep | null;
  authorCaricaId?: { _id: string; nome: string } | null;
  stats: {
    total: number;
    checkedIn: number;
    emailSent: number;
  };
}

interface AssociateRow {
  _id: string;
  nominativo: string;
  codice: string;
  provincia: string;
  email: string;
  categoria: string;
  isInvited: boolean;
  inRegola: boolean;
}

interface InvitationRow {
  _id: string;
  associateId: {
    nominativo: string;
    codice: string;
    provincia: string;
    email: string;
  } | null;
  /* Snapshot per gli inviti "per carica" (titolari di carica, email personale) */
  recipientEmail?: string | null;
  recipientName?: string | null;
  emailSentAt: string | null;
  reminderSentAt: string | null;
  checkedIn: boolean;
  checkedInAt: string | null;
}

// -- Badge styles --

const statoLabel: Record<string, string> = {
  BOZZA: 'Bozza',
  PUBBLICATO: 'Pubblicato',
  IN_CORSO: 'In corso',
  CONCLUSO: 'Concluso',
  ANNULLATO: 'Annullato',
};

// Mappatura stato → classe badge del design system (.assoc-app .badge.s-*)
const statoBadgeClass: Record<string, string> = {
  BOZZA: 's-bozza',
  PUBBLICATO: 's-programmato',
  IN_CORSO: 's-inviato',
  CONCLUSO: 's-concluso',
  ANNULLATO: '',
};

// Valid state transitions
const statoTransitions: Record<string, string[]> = {
  BOZZA: ['PUBBLICATO'],
  PUBBLICATO: ['IN_CORSO', 'ANNULLATO'],
  IN_CORSO: ['CONCLUSO', 'ANNULLATO'],
  CONCLUSO: [],
  ANNULLATO: [],
};

// -- Helpers --

function formatDate(dateStr?: string) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('it-IT', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  });
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// -- Regia: fasi del ciclo di vita evento --

type PhaseKey = 'dati' | 'invitati' | 'pubblica' | 'presenze' | 'verbale' | '';

/** Selezione degli invitati non ancora inviata, conservata per evento nella sessione del browser. */
interface SelezioneInviti {
  inviteTutti: boolean;
  tuttiStatoFilter: '' | 'inregola' | 'nonregola';
  tuttiProvincia: string;
  inviteCariche: string[];
  selectedIds: string[];
  excludedIds: string[];
}
const chiaveSelezioneInviti = (eventId: string) => `evento-selezione-inviti-${eventId}`;
function leggiSelezioneInviti(eventId: string | undefined): SelezioneInviti | null {
  if (!eventId) return null;
  try {
    const grezzo = sessionStorage.getItem(chiaveSelezioneInviti(eventId));
    if (!grezzo) return null;
    const v = JSON.parse(grezzo);
    const elenco = (x: unknown) => (Array.isArray(x) ? x.filter((y): y is string => typeof y === 'string') : []);
    return {
      inviteTutti: v?.inviteTutti === true,
      tuttiStatoFilter: v?.tuttiStatoFilter === 'inregola' || v?.tuttiStatoFilter === 'nonregola' ? v.tuttiStatoFilter : '',
      tuttiProvincia: typeof v?.tuttiProvincia === 'string' ? v.tuttiProvincia : '',
      inviteCariche: elenco(v?.inviteCariche),
      selectedIds: elenco(v?.selectedIds),
      excludedIds: elenco(v?.excludedIds),
    };
  } catch {
    return null;
  }
}
function scriviSelezioneInviti(eventId: string | undefined, sel: SelezioneInviti): void {
  if (!eventId) return;
  try {
    const vuota = !sel.inviteTutti && sel.inviteCariche.length === 0 && sel.selectedIds.length === 0;
    if (vuota) sessionStorage.removeItem(chiaveSelezioneInviti(eventId));
    else sessionStorage.setItem(chiaveSelezioneInviti(eventId), JSON.stringify(sel));
  } catch {
    /* archivio del browser non disponibile: la selezione resta solo in pagina */
  }
}

// Nodo dello stepper verticale (design system .assoc-app .step-node).
// Definito a livello di modulo: se fosse interno al componente verrebbe
// ricreato ad ogni render, smontando il sottoalbero (perdita focus, reset scroll).
// La logica di stato fase resta invariata: `stato` è 'done'|'current'|'todo',
// `open` indica la fase visualizzata (mappata a data-state="active").
const PhaseRow: React.FC<{
  numero: number;
  titolo: string;
  descrizione?: string;
  stato: 'done' | 'current' | 'todo';
  open: boolean;
  onToggle: () => void;
}> = ({ numero, titolo, descrizione, stato, open, onToggle }) => {
  const dataState = open ? 'active' : stato === 'done' ? 'done' : 'todo';
  return (
    <button
      type="button"
      className="step-node"
      data-state={dataState}
      aria-current={open ? 'step' : undefined}
      onClick={onToggle}
    >
      <span className="rail">
        <span className="bullet">{dataState === 'done' ? <Check size={15} /> : numero}</span>
        <span className="line" />
      </span>
      <span className="body">
        <span className="t" style={{ display: 'block' }}>{titolo}</span>
        {descrizione && <span className="d" style={{ display: 'block' }}>{descrizione}</span>}
      </span>
    </button>
  );
};

const AssociativeEventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Link delle push dei verbali ai rappresentanti («Trascrizione pronta», «Verbale disponibile»):
  // `?sezione=verbale` (l'app lo traduce nella sua schermata); sul sito si apre la pagina del verbale.
  const apriVerbale = new URLSearchParams(location.search).get('sezione') === 'verbale';
  useEffect(() => {
    if (apriVerbale && id) navigate(`/area-associativa/calendario/${id}/verbale`, { replace: true });
  }, [apriVerbale, id, navigate]);

  // Core state
  const [event, setEvent] = useState<EventData | null>(null);
  const [openPhase, setOpenPhase] = useState<PhaseKey>('dati');
  const [loading, setLoading] = useState(true);
  const phaseInitRef = useRef(false);
  const contenutoFaseRef = useRef<HTMLDivElement>(null);

  // Associates tab state
  const [associates, setAssociates] = useState<AssociateRow[]>([]);
  const [assocTotal, setAssocTotal] = useState(0);
  const [assocPage, setAssocPage] = useState(1);
  const [assocTotalPages, setAssocTotalPages] = useState(0);
  const [assocSearch, setAssocSearch] = useState('');
  const [debouncedAssocSearch, setDebouncedAssocSearch] = useState('');
  // Filtro stato tesseramento: '' = tutti | 'inregola' (pagato/attivato) | 'nonregola'
  const [assocStatoFilter, setAssocStatoFilter] = useState<'' | 'inregola' | 'nonregola'>('');
  // Filtro provincia (visualizzazione + ramo "tutti"); lista province dello scope
  const [assocProvincia, setAssocProvincia] = useState('');
  const [provinceOptions, setProvinceOptions] = useState<string[]>([]);
  const [assocLoading, setAssocLoading] = useState(false);
  // 403 dal picker: nessuna carica con il trio associati.read+scope+settore
  const [assocForbidden, setAssocForbidden] = useState<string | null>(null);

  // Tab Presenze: lista completa inviti + ricerca locale
  const [presenze, setPresenze] = useState<InvitationRow[]>([]);
  const [presenzeTotal, setPresenzeTotal] = useState(0);
  const [presenzeLoading, setPresenzeLoading] = useState(false);
  const [presenzeSearch, setPresenzeSearch] = useState('');

  // Allegati invito: upload in corso
  const [uploadingAllegato, setUploadingAllegato] = useState(false);

  // Selezione degli invitati non ancora inviata: resta nella sessione del browser, così uscendo
  // dalla pagina («Modifica dati», tasto Indietro) e tornando la scelta non si perde.
  const selezioneSalvata = useRef(leggiSelezioneInviti(id)).current;
  // Gruppi rapidi invitati (spec 2026-06-11)
  const [inviteTutti, setInviteTutti] = useState(selezioneSalvata?.inviteTutti ?? false);
  // Filtri applicati alla modalità "tutti" (indipendenti dalla tendina di sola
  // visualizzazione): stato tesseramento e provincia di invito
  const [tuttiStatoFilter, setTuttiStatoFilter] = useState<'' | 'inregola' | 'nonregola'>(selezioneSalvata?.tuttiStatoFilter ?? '');
  const [tuttiProvincia, setTuttiProvincia] = useState(selezioneSalvata?.tuttiProvincia ?? '');
  const [inviteCariche, setInviteCariche] = useState<string[]>(selezioneSalvata?.inviteCariche ?? []);
  const [caricheOptions, setCaricheOptions] = useState<{ _id: string; nome: string }[]>([]);
  const [invitePreview, setInvitePreview] = useState<{
    totale: number; associati: number; rappresentanti: number;
    duplicatiRimossi: number; senzaEmail: number; giaInvitati: number;
    perCarica: { nome: string; count: number }[];
  } | null>(null);
  const [loadingInvitePreview, setLoadingInvitePreview] = useState(false);
  // Elenco nominativo destinatari (on-demand dal bottone "Mostra")
  const [recipientList, setRecipientList] = useState<{
    associati: { nominativo: string; codice?: string; email: string | null }[];
    cariche: { nominativo: string; email: string | null }[];
    troncato: boolean;
  } | null>(null);
  const [loadingRecipientList, setLoadingRecipientList] = useState(false);
  const assocDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Invitations tab state

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(selezioneSalvata?.selectedIds ?? []));
  // Associati esclusi a mano quando "tutto il perimetro" è attivo
  const [excludedIds, setExcludedIds] = useState<Set<string>>(() => new Set(selezioneSalvata?.excludedIds ?? []));

  useEffect(() => {
    scriviSelezioneInviti(id, {
      inviteTutti, tuttiStatoFilter, tuttiProvincia, inviteCariche,
      selectedIds: Array.from(selectedIds), excludedIds: Array.from(excludedIds),
    });
  }, [id, inviteTutti, tuttiStatoFilter, tuttiProvincia, inviteCariche, selectedIds, excludedIds]);

  // Action states
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Menù "Altro": può essere aperto dalla testata ('top') o dalle azioni in
  // fondo alla fase Invitati ('bottom')
  const [altroOpen, setAltroOpen] = useState<'top' | 'bottom' | null>(null);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // Verbale: qui solo riepilogo (autore) o lettura (altri); editor e assistente
  // sono nella pagina dedicata /area-associativa/calendario/:id/verbale
  const [verbaleContenuto, setVerbaleContenuto] = useState('');
  const [verbaleAllegati, setVerbaleAllegati] = useState<any[]>([]);
  const [verbaleIsOwner, setVerbaleIsOwner] = useState(false);
  const [verbaleUltimaModifica, setVerbaleUltimaModifica] = useState<string | null>(null);
  const [verbaleLoading, setVerbaleLoading] = useState(false);
  const [verbaleBozza, setVerbaleBozza] = useState(true);
  const [verbalePubblicatoAt, setVerbalePubblicatoAt] = useState<string | null>(null);
  const [pdfVerbaleLoading, setPdfVerbaleLoading] = useState(false);

  // Profile for layout (from localStorage)
  const [repProfile, setRepProfile] = useState<{ nome?: string; cognome?: string; caricaPrincipale?: string; tipo?: 'interno' | 'esterno'; foto?: string }>({});

  useEffect(() => {
    try {
      const stored = localStorage.getItem('representative_profile');
      if (stored) setRepProfile(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  // Cariche per i gruppi rapidi inviti (solo per l'autore, che ha eventi.create)
  useEffect(() => {
    if (!event?.isOwner) return;
    getRepEventCaricheOptions()
      .then(res => { if (res.success) setCaricheOptions(res.data || []); })
      .catch(() => { /* resta la selezione singola */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.isOwner]);

  // -- Data Loading --

  // silent=true per i refresh post-azione: aggiorna i dati senza far comparire
  // lo spinner a tutta pagina (il guard `if (loading)` sostituirebbe l'intera regia)
  const fetchEvent = useCallback(async (silent = false) => {
    if (!id) return;
    try {
      if (!silent) setLoading(true);
      const res = await getRepEvent(id);
      if (res.success) {
        setEvent(res.data);
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        removeRepresentativeToken();
        navigate('/accesso-associati');
        return;
      }
      toast.error('Errore nel caricamento dell\'evento');
      navigate('/area-associativa/calendario');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    fetchEvent(false);
  }, [fetchEvent]);

  // All'arrivo dell'evento, apri la fase "corrente" una sola volta
  useEffect(() => {
    if (!event || phaseInitRef.current) return;
    phaseInitRef.current = true;
    if (!event.isOwner) { setOpenPhase('dati'); return; }
    setOpenPhase(event.stato === 'BOZZA' ? 'invitati' : 'presenze');
  }, [event]);

  // Debounce associates search
  useEffect(() => {
    if (assocDebounceRef.current) clearTimeout(assocDebounceRef.current);
    assocDebounceRef.current = setTimeout(() => {
      setDebouncedAssocSearch(assocSearch);
      setAssocPage(1);
    }, 300);
    return () => { if (assocDebounceRef.current) clearTimeout(assocDebounceRef.current); };
  }, [assocSearch]);

  const fetchAssociates = useCallback(async () => {
    if (!id) return;
    try {
      setAssocLoading(true);
      const params: Record<string, string> = { page: String(assocPage), limit: '20' };
      if (debouncedAssocSearch) params.search = debouncedAssocSearch;
      if (assocStatoFilter) params.statoFilter = assocStatoFilter;
      if (assocProvincia) params.provincia = assocProvincia;
      const res = await getRepEventAssociates(id, params);
      if (res.success) {
        setAssociates(res.data || []);
        setAssocTotal(res.total || 0);
        setAssocTotalPages(res.totalPages || 0);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setAssocForbidden(err.response?.data?.error || 'Permessi insufficienti per vedere gli associati');
      } else {
        toast.error('Errore caricamento associati');
      }
    } finally {
      setAssocLoading(false);
    }
  }, [id, assocPage, debouncedAssocSearch, assocStatoFilter, assocProvincia, toast]);

  // Province dello scope (per il filtro provincia): caricate una volta in fase Invitati
  useEffect(() => {
    if (openPhase !== 'invitati' || !event?.isOwner || !id) return;
    if (provinceOptions.length > 0) return;
    getRepEventAssociateProvince(id)
      .then(res => { if (res.success) setProvinceOptions(res.data || []); })
      .catch(() => { /* silenzioso: il filtro provincia semplicemente non compare */ });
  }, [openPhase, event?.isOwner, id, provinceOptions.length]);

  // Load tab data
  useEffect(() => {
    if (openPhase === 'invitati' && event?.isOwner) {
      fetchAssociates();
    }
  }, [openPhase, event?.isOwner, fetchAssociates]);

  // Tab Presenze: lista completa (non paginata) per il check-in manuale
  const fetchPresenze = useCallback(async () => {
    if (!id) return;
    try {
      setPresenzeLoading(true);
      const res = await getRepEventInvitations(id, { page: '1', limit: '5000' });
      if (res.success) { setPresenze(res.data || []); setPresenzeTotal(res.total || 0); }
    } catch {
      toast.error('Errore caricamento presenze');
    } finally {
      setPresenzeLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (openPhase === 'presenze' && event?.isOwner) fetchPresenze();
  }, [openPhase, event?.isOwner, fetchPresenze]);

  // Allegati invito (PDF/immagini linkati nell'email) — distinti dagli allegati del verbale
  const handleUploadAllegatoInvito = async (file: File | null) => {
    if (!id || !file) return;
    setUploadingAllegato(true);
    try {
      const res = await uploadRepEventAllegato(id, file);
      if (res.success) {
        toast.success('Allegato caricato');
        fetchEvent(true);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Errore caricamento allegato');
    } finally {
      setUploadingAllegato(false);
    }
  };

  const handleDeleteAllegatoInvito = async (allegatoId: string) => {
    if (!id) return;
    try {
      const res = await deleteRepEventAllegato(id, allegatoId);
      if (res.success) {
        toast.success('Allegato rimosso');
        fetchEvent(true);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Errore rimozione allegato');
    }
  };

  // Load verbale data (riepilogo/lettura: la chat dell'assistente non serve qui)
  useEffect(() => {
    if (openPhase !== 'verbale' || !id) return;
    const loadVerbale = async () => {
      setVerbaleLoading(true);
      try {
        const res = await getRepEventVerbale(id);
        if (res.success) {
          setVerbaleContenuto(res.data.contenuto || '');
          setVerbaleAllegati(res.data.allegati || []);
          setVerbaleIsOwner(res.data.isOwner);
          setVerbaleUltimaModifica(res.data.ultimaModifica);
          setVerbaleBozza(res.data.bozza !== false);
          setVerbalePubblicatoAt(res.data.pubblicatoAt || null);
        }
      } catch { /* ignore */ }
      setVerbaleLoading(false);
    };
    loadVerbale();
  }, [openPhase, id]);

  // -- Actions --

  const handleChangeStato = async (newStato: string) => {
    if (!id || !event) return;
    setAltroOpen(null);
    try {
      const res = await updateRepEventStato(id, newStato);
      if (res.success) {
        // refetch completo: lifecycle/CTA derivano da event.stato + stats, che
        // devono restare coerenti dopo la transizione
        await fetchEvent(true);
        toast.success(`Stato aggiornato a "${statoLabel[newStato] || newStato}"`);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Errore cambio stato');
    }
  };

  const handleDelete = () => {
    if (!id || !event) return;
    setConfirmDialog({
      isOpen: true,
      title: event.stato === 'BOZZA' ? 'Elimina evento' : 'Annulla evento',
      message: event.stato === 'BOZZA'
        ? 'Sei sicuro di voler eliminare questo evento? L\'azione e irreversibile.'
        : 'Sei sicuro di voler annullare questo evento?',
      type: 'danger',
      onConfirm: async () => {
        try {
          if (event.stato === 'BOZZA') {
            await deleteRepEvent(id);
            toast.success('Evento eliminato');
            navigate('/area-associativa/calendario');
          } else {
            await updateRepEventStato(id, 'ANNULLATO');
            await fetchEvent(true);
            toast.success('Evento annullato');
          }
        } catch (err: any) {
          toast.error(err.response?.data?.error || 'Errore operazione');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  // Selection handlers
  const toggleSelect = (assocId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(assocId)) next.delete(assocId);
      else next.add(assocId);
      return next;
    });
    setInvitePreview(null);
  };

  // Entra in modalità "tutti" con specifici filtri (stato + provincia) per
  // l'invito (NON tocca le tendine di sola visualizzazione). Azzera selezioni
  // puntuali ed esclusioni.
  const enterTutti = (statoForInvite: '' | 'inregola' | 'nonregola', provinciaForInvite: string) => {
    setInviteTutti(true);
    setTuttiStatoFilter(statoForInvite);
    setTuttiProvincia(provinciaForInvite);
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setInvitePreview(null);
  };

  const clearTutti = () => {
    setInviteTutti(false);
    setExcludedIds(new Set());
    setSelectedIds(new Set());
    setInvitePreview(null);
  };

  // "Seleziona tutti" della card: seleziona tutti quelli VISUALIZZATI dai
  // filtri correnti (stato + provincia).
  const selectAll = () => enterTutti(assocStatoFilter, assocProvincia);
  const deselectAll = () => clearTutti();

  // true se l'associato rientra nei filtri (stato + provincia) della modalità "tutti"
  const matchTuttiFilter = (a: AssociateRow) => {
    const okStato = tuttiStatoFilter === '' ? true : (tuttiStatoFilter === 'inregola' ? a.inRegola : !a.inRegola);
    const okProv = tuttiProvincia === '' ? true : a.provincia === tuttiProvincia;
    return okStato && okProv;
  };

  // Esclude/reinserisce un associato quando si parte da "tutto il perimetro"
  const toggleExcluded = (assocId: string) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(assocId)) next.delete(assocId);
      else next.add(assocId);
      return next;
    });
    setInvitePreview(null);
  };

  const inviteSelection = useCallback(() => ({
    associateIds: Array.from(selectedIds),
    tuttiAssociati: inviteTutti,
    cariche: inviteCariche,
    // il filtro stato dell'invito è quello della modalità "tutti", NON la
    // tendina di sola visualizzazione
    statoFilter: inviteTutti ? (tuttiStatoFilter || undefined) : undefined,
    provincia: inviteTutti ? (tuttiProvincia || undefined) : undefined,
    excludeAssociateIds: inviteTutti ? Array.from(excludedIds) : undefined,
  }), [selectedIds, inviteTutti, inviteCariche, tuttiStatoFilter, tuttiProvincia, excludedIds]);

  const hasInviteSelection = selectedIds.size > 0 || inviteTutti || inviteCariche.length > 0;

  const loadInvitePreview = useCallback(async () => {
    if (!id) return;
    setLoadingInvitePreview(true);
    try {
      const res = await getRepEventInvitationsPreview(id, inviteSelection());
      if (res.success) setInvitePreview(res.data);
    } catch {
      // anteprima live: silenziosa sui singoli errori
    } finally {
      setLoadingInvitePreview(false);
    }
  }, [id, inviteSelection]);

  // Carica l'elenco nominativo dei destinatari (bottone "Mostra elenco")
  const loadRecipientList = useCallback(async () => {
    if (!id) return;
    setLoadingRecipientList(true);
    try {
      const res = await getRepEventInvitationsPreview(id, { ...inviteSelection(), includeList: true });
      if (res.success && res.data?.elenco) setRecipientList(res.data.elenco);
    } catch {
      toast.error('Errore caricamento elenco destinatari');
    } finally {
      setLoadingRecipientList(false);
    }
  }, [id, inviteSelection, toast]);

  // Contatore invitati sempre aggiornato (debounce) quando si è nella fase Invitati.
  // Ogni cambio di selezione invalida l'elenco nominativo già mostrato.
  // L'elenco nominativo si chiude solo se la selezione cambia, non quando si chiude e si riapre la fase.
  const chiaveSelezioneAttuale = JSON.stringify([
    inviteTutti, tuttiStatoFilter, tuttiProvincia, inviteCariche, Array.from(selectedIds), Array.from(excludedIds),
  ]);
  const chiaveElencoRef = useRef(chiaveSelezioneAttuale);
  useEffect(() => {
    if (chiaveElencoRef.current === chiaveSelezioneAttuale) return;
    chiaveElencoRef.current = chiaveSelezioneAttuale;
    setRecipientList(null);
  }, [chiaveSelezioneAttuale]);
  useEffect(() => {
    if (openPhase !== 'invitati' || !event?.isOwner) return;
    if (!hasInviteSelection) { setInvitePreview(null); return; }
    const t = setTimeout(() => { loadInvitePreview(); }, 400);
    return () => clearTimeout(t);
  }, [openPhase, event?.isOwner, hasInviteSelection, selectedIds, inviteTutti, inviteCariche, excludedIds, tuttiStatoFilter, tuttiProvincia, loadInvitePreview]);

  // Invia gli inviti mancanti. Se c'è una selezione non ancora generata
  // (es. nuovi invitati aggiunti dopo la pubblicazione), la genera
  // automaticamente prima dell'invio: un solo passaggio per l'utente.
  const handleSendInvitations = () => {
    if (!id) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Invia inviti',
      message: hasInviteSelection
        ? 'Gli inviti per i destinatari appena selezionati verranno generati e inviati subito (email, notifiche e push), insieme a quelli ancora in attesa di invio.'
        : 'Vuoi inviare email, notifiche e push a tutti gli invitati che non li hanno ancora ricevuti?',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        setSending(true);
        try {
          if (hasInviteSelection) {
            await generateRepEventInvitations(id, inviteSelection());
            setSelectedIds(new Set());
            setInviteTutti(false);
            setInviteCariche([]);
            setInvitePreview(null);
            fetchAssociates();
          }
          const res = await sendRepEventInvitations(id);
          if (res.success) {
            toast.success(`${res.sent} inviti inviati`);
            fetchEvent(true);
          }
        } catch (err: any) {
          toast.error(err.response?.data?.error || 'Errore invio inviti');
          // refresh anche su fallimento parziale (inviti generati ma invio
          // fallito): contatori e CTA riflettono lo stato reale
          fetchEvent(true);
        } finally {
          setSending(false);
        }
      },
    });
  };

  const handleSendReminder = () => {
    if (!id) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Invia promemoria',
      message: 'Vuoi inviare un promemoria a tutti gli invitati?',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        setSending(true);
        try {
          const res = await sendRepEventReminder(id);
          if (res.success) {
            toast.success(`${res.sent} promemoria inviati`);
          }
        } catch (err: any) {
          toast.error(err.response?.data?.error || 'Errore invio promemoria');
        } finally {
          setSending(false);
        }
      },
    });
  };

  // -- Regia: pubblica e invia (azione primaria adattiva) --

  const doPublishAndSend = async () => {
    if (!id) return;
    setGenerating(true);
    try {
      if (hasInviteSelection) await generateRepEventInvitations(id, inviteSelection());
      await sendRepEventInvitations(id);
      await updateRepEventStato(id, 'PUBBLICATO');
      toast.success('Evento pubblicato e inviti inviati');
      setSelectedIds(new Set());
      setInviteTutti(false);
      setInviteCariche([]);
      setInvitePreview(null);
      // await: lo stato/CTA si aggiornano prima che il bottone si riabiliti (no doppio-submit)
      await fetchEvent(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Errore durante pubblicazione/invio');
      // refresh anche su fallimento parziale (inviti generati ma invio/pubblicazione
      // falliti): così CTA e conteggi riflettono lo stato reale
      fetchEvent(true);
    } finally {
      setGenerating(false);
    }
  };

  const handlePublishAndSend = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Pubblicare e inviare gli inviti?',
      message: 'Verranno inviate le email e le relative notifiche. L\'evento diventerà visibile ai destinatari.',
      type: 'info',
      onConfirm: () => { setConfirmDialog(prev => ({ ...prev, isOpen: false })); doPublishAndSend(); },
    });
  };

  const handlePublishOnly = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Pubblicare l\'evento?',
      message: 'L\'evento diventerà visibile. Potrai invitare le persone anche in un secondo momento.',
      type: 'info',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        if (!id) return;
        setSending(true);
        try {
          await updateRepEventStato(id, 'PUBBLICATO');
          toast.success('Evento pubblicato');
          await fetchEvent(true);
        } catch (err: any) {
          toast.error(err?.response?.data?.error || 'Errore durante pubblicazione');
        } finally {
          setSending(false);
        }
      },
    });
  };

  // -- Verbale --

  const UPLOADS_BASE = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:5000';

  // Il PDF richiede il token del rappresentante: si scarica come blob (repAxios)
  // e si salva con un link temporaneo (l'URL semplice non porterebbe il token).
  const handleScaricaPdfVerbale = async () => {
    if (!id || pdfVerbaleLoading) return;
    setPdfVerbaleLoading(true);
    try {
      const blob = await scaricaPdfVerbale(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Verbale - ${(event?.nome || 'evento').replace(/[\\/:*?"<>|]+/g, ' ').trim()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      toast.error(await messaggioErrorePdf(err));
    } finally {
      setPdfVerbaleLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Apre/chiude una fase (toggle accordion)
  // Il contenuto della fase sta nella colonna accanto: un clic sulla fase aperta non la chiude (lascerebbe la colonna vuota).
  const apriFase = (k: PhaseKey) => () => setOpenPhase(k);

  // -- Loading / Error --

  if (loading) {
    return (
      <AssociativeAppShell route="calendario" nome={repProfile.nome} cognome={repProfile.cognome} caricaPrincipale={repProfile.caricaPrincipale} tipo={repProfile.tipo} foto={repProfile.foto}>
        <div className="page">
          <div className="muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--un-green-500)' }} />
          </div>
        </div>
      </AssociativeAppShell>
    );
  }

  if (!event) {
    return (
      <AssociativeAppShell route="calendario" nome={repProfile.nome} cognome={repProfile.cognome} caricaPrincipale={repProfile.caricaPrincipale} tipo={repProfile.tipo} foto={repProfile.foto}>
        <div className="page">
          <div className="muted" style={{ textAlign: 'center', padding: '80px 0', fontSize: '13px' }}>Evento non trovato</div>
        </div>
      </AssociativeAppShell>
    );
  }

  const pendingInvitations = event.stats.total - event.stats.emailSent;
  const availableTransitions = statoTransitions[event.stato] || [];

  // Azione primaria della regia (CTA adattivo)
  const lifecycle = getEventLifecycleAction(event.stato, hasInviteSelection, pendingInvitations);

  // Verbale: testo visibile (o immagini) o almeno un allegato (autore); in più pubblicato per i
  // lettori, stessa regola di backend/utils/verbaleLettura.js. Il testo lo scrive un rappresentante:
  // elenco di consentiti (utils/sanitizeHtml.ts) prima di decidere e di mostrarlo.
  const verbaleContenutoSicuro = sanitizeHtml(verbaleContenuto || '');
  const verbaleConTesto = haTestoVisibile(verbaleContenutoSicuro);
  const verbaleScritto = verbaleConTesto || verbaleAllegati.length > 0;
  const verbaleLeggibile = !verbaleBozza && verbaleScritto;

  // Stato di avanzamento di ogni fase (per le icone ✓/▸/○)
  const phaseState =(k: PhaseKey): 'done' | 'current' | 'todo' => {
    const stato = event.stato;
    if (k === 'dati') return 'done';
    if (k === 'invitati') return event.stats.total > 0 ? 'done' : (stato === 'BOZZA' ? 'current' : 'todo');
    if (k === 'pubblica') return stato !== 'BOZZA' ? 'done' : 'current';
    if (k === 'presenze') return (event.stats?.checkedIn ?? 0) > 0 ? 'done' : 'todo';
    if (k === 'verbale') return verbaleScritto ? 'done' : 'todo';
    return 'todo';
  };

  // CTA adattivo + menù Altro. Riusato in testata ('top') e in fondo alla
  // fase Invitati ('bottom', con menù che si apre verso l'alto).
  const renderOwnerActions = (pos: 'top' | 'bottom') => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      {lifecycle.kind === 'publish-and-send' && (
        <button type="button" className="btn primary" onClick={handlePublishAndSend} disabled={sending || generating}>
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone size={15} />}
          Pubblica e invia {lifecycle.pendingInvites > 0 ? `${lifecycle.pendingInvites} ` : ''}inviti
        </button>
      )}
      {lifecycle.kind === 'publish-only' && (
        <button type="button" className="btn primary" onClick={handlePublishOnly} disabled={sending}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone size={15} />}
          Pubblica evento
        </button>
      )}
      {lifecycle.kind === 'send-new' && (() => {
        const totNuovi = lifecycle.pendingInvites + (hasInviteSelection ? (invitePreview?.totale ?? 0) : 0);
        return (
          <button type="button" className="btn primary" onClick={handleSendInvitations} disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={15} />}
            Invia inviti ai nuovi{totNuovi > 0 ? ` (${totNuovi})` : ''}
          </button>
        );
      })()}

      {/* Menù Altro (azioni rare) */}
      <div style={{ position: 'relative' }}>
        <button type="button" className="btn ghost" onClick={() => setAltroOpen(o => (o === pos ? null : pos))}>
          <MoreHorizontal size={15} /> Altro
        </button>
        {altroOpen === pos && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setAltroOpen(null)} />
            <div className={`absolute right-0 ${pos === 'bottom' ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 min-w-[190px] py-1`}>
              {event.stato !== 'ANNULLATO' && event.stato !== 'CONCLUSO' && (
                <button
                  onClick={() => { setAltroOpen(null); navigate(`/area-associativa/calendario/${id}/modifica`); }}
                  className="w-full text-left px-3 py-2 text-2xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-2"
                >
                  <Pencil className="w-3 h-3" /> Modifica dati
                </button>
              )}
              {availableTransitions.filter(s => s !== 'PUBBLICATO').map(s => (
                <button
                  key={s}
                  onClick={() => handleChangeStato(s)}
                  className="w-full text-left px-3 py-2 text-2xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Segna come <span className="font-medium">{statoLabel[s] || s}</span>
                </button>
              ))}
              {(event.stato === 'BOZZA' || event.stato === 'PUBBLICATO') && (
                <button
                  onClick={() => { setAltroOpen(null); handleDelete(); }}
                  className="w-full text-left px-3 py-2 text-2xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 inline-flex items-center gap-2"
                >
                  <Trash2 className="w-3 h-3" /> {event.stato === 'BOZZA' ? 'Elimina evento' : 'Annulla evento'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // -- Tab: Panoramica --

  const renderPanoramica = () => (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <section className="card">
        <div className="card-head">
          <FileText size={16} />
          <h2>Dati evento</h2>
        </div>
        <div className="card-body">
          <div className="detail-list">
            {/* Data e orario */}
            <div className="drow">
              <div className="ic"><Calendar size={16} /></div>
              <div>
                <div className="k">Data e orario</div>
                <div className="v">
                  {formatDate(event.dataEvento)}
                  {(event.oraInizio || event.oraFine) && (
                    <small>{event.oraInizio || '--:--'}{event.oraFine ? ` - ${event.oraFine}` : ''}</small>
                  )}
                </div>
              </div>
            </div>

            {/* Luogo */}
            {event.luogo && (
              <div className="drow">
                <div className="ic"><MapPin size={16} /></div>
                <div>
                  <div className="k">Luogo</div>
                  <div className="v">{event.luogo}{event.indirizzo && <small>{event.indirizzo}</small>}</div>
                  {event.linkMappa && (
                    <a
                      href={event.linkMappa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="top-link"
                      style={{ padding: '4px 0', color: 'var(--un-green-700)' }}
                    >
                      <ExternalLink size={13} /> Apri in Google Maps
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Descrizione */}
            {event.descrizione && (
              <div className="drow">
                <div className="ic"><FileText size={16} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="k">{Array.isArray(event.ordineDelGiorno) ? 'Tema' : 'Descrizione'}</div>
                  <div className="v" style={{ fontWeight: 400, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{event.descrizione}</div>
                </div>
              </div>
            )}

            {/* Ordine del giorno (campo apposito) */}
            {Array.isArray(event.ordineDelGiorno) && event.ordineDelGiorno.length > 0 && (
              <div className="drow">
                <div className="ic"><ListOrdered size={16} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="k">Ordine del giorno</div>
                  <ol className="v" style={{ fontWeight: 400, lineHeight: 1.6, margin: 0, paddingLeft: '20px', listStyle: 'decimal' }}>
                    {event.ordineDelGiorno.map((p, i) => <li key={i}>{p}</li>)}
                  </ol>
                </div>
              </div>
            )}

            {/* Allegati invito (link pubblici, visibili anche ai non-organizzatori) */}
            {(event.allegatiInvito?.length || 0) > 0 && (
              <div className="drow">
                <div className="ic"><Paperclip size={16} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="k">Allegati</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                    {event.allegatiInvito!.map(a => (
                      <a
                        key={a._id}
                        href={`${UPLOADS_BASE}/uploads/event-invites/${event._id}/${encodeURIComponent(a.filename)}`}
                        target="_blank" rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', background: 'var(--un-ink-50)', textDecoration: 'none', color: 'var(--text-body)' }}
                      >
                        <FileText size={14} />
                        <span style={{ font: '500 12.5px var(--font-body)' }}>{a.originalName}</span>
                        <span className="muted" style={{ fontSize: '11px' }}>({Math.round(a.size / 1024)} KB)</span>
                        <Download size={13} style={{ marginLeft: 'auto' }} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Contenuto completo dell'evento (testo ricco) */}
            {event.emailContent && (
              <div className="drow">
                <div className="ic"><FileText size={16} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="k">Contenuto</div>
                  <div
                    className="v break-words prose prose-sm dark:prose-invert max-w-none [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_iframe]:max-w-full [&_iframe]:w-full [&_table]:!w-full [&_table]:!max-w-full [&_*]:max-w-full"
                    style={{ fontWeight: 400, lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: event.emailContent }}
                  />
                </div>
              </div>
            )}

            {/* Autore */}
            {event.authorRepresentativeId && (
              <div className="drow">
                <div className="ic"><Users size={16} /></div>
                <div>
                  <div className="k">Creato da</div>
                  <div className="v">{event.authorRepresentativeId.nome} {event.authorRepresentativeId.cognome}</div>
                </div>
              </div>
            )}

            {/* Carica autrice (snapshot, determina la visibilità) */}
            {event.authorCaricaId?.nome && (
              <div className="drow">
                <div className="ic"><Shield size={16} /></div>
                <div>
                  <div className="k">Pubblicato come</div>
                  <div className="v">{event.authorCaricaId.nome}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* KPI reali */}
      <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi"><div className="n">{event.stats.total}</div><div className="l">Invitati</div></div>
        <div className="kpi"><div className="n">{event.stats.emailSent}</div><div className="l">Email inviate</div></div>
        <div className="kpi"><div className="n">{event.stats.checkedIn}</div><div className="l">Presenti</div></div>
      </div>
    </div>
  );

  // -- Tab: Presenze (check-in manuale dell'organizzatore) --

  const togglePresenza = async (inv: InvitationRow) => {
    if (!id) return;
    try {
      const res = await toggleRepEventCheckin(id, inv._id);
      if (res.success) {
        setPresenze(prev => prev.map(p => p._id === inv._id
          ? { ...p, checkedIn: res.checkedIn, checkedInAt: res.checkedIn ? new Date().toISOString() : null }
          : p));
        fetchEvent(true);
      }
    } catch {
      toast.error('Errore aggiornamento presenza');
    }
  };

  // Lista presenze attualmente visibile (rispetta la ricerca): le azioni rapide
  // DEVONO agire solo su ciò che l'utente vede, non sull'intero evento.
  const visiblePresenze = () => {
    const term = presenzeSearch.trim().toLowerCase();
    if (!term) return presenze;
    return presenze.filter(p =>
      (p.recipientName || p.associateId?.nominativo || '').toLowerCase().includes(term) ||
      (p.associateId?.codice || '').toLowerCase().includes(term) ||
      (p.recipientEmail || p.associateId?.email || '').toLowerCase().includes(term));
  };

  // Azioni rapide presenze: cambiano solo gli inviti visibili che ne hanno bisogno
  const markAllPresent = async () => {
    if (!id) return;
    const toMark = visiblePresenze().filter(p => !p.checkedIn);
    if (toMark.length === 0) return;
    setPresenzeLoading(true);
    try {
      for (const inv of toMark) { await toggleRepEventCheckin(id, inv._id); }
    } catch {
      toast.error('Errore aggiornamento presenze: riprova sulle rimanenti');
    } finally {
      // Riallinea SEMPRE dal server (anche su errore a metà): così un retry
      // ricalcola "toMark" sullo stato reale e non ri-toggola chi è già presente.
      await fetchPresenze();
      fetchEvent(true);
      setPresenzeLoading(false);
    }
  };

  const clearAllPresent = async () => {
    if (!id) return;
    const toClear = visiblePresenze().filter(p => p.checkedIn);
    if (toClear.length === 0) return;
    setPresenzeLoading(true);
    try {
      for (const inv of toClear) { await toggleRepEventCheckin(id, inv._id); }
    } catch {
      toast.error('Errore aggiornamento presenze: riprova sulle rimanenti');
    } finally {
      await fetchPresenze();
      fetchEvent(true);
      setPresenzeLoading(false);
    }
  };

  const renderPresenze = () => {
    const term = presenzeSearch.trim().toLowerCase();
    const filtered = visiblePresenze();
    const presentiCount = presenze.filter(p => p.checkedIn).length;
    const totale = presenzeTotal || presenze.length;
    const pct = totale > 0 ? Math.round((presentiCount / totale) * 100) : 0;
    const truncated = presenzeTotal > presenze.length;

    return (
      <div className="fade-in">
        <section className="card">
          <div className="card-head">
            <CheckCircle size={16} />
            <h2>Registro presenze</h2>
            {presenze.length > 0 && (
              <div className="aux" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="btn soft sm" type="button" onClick={markAllPresent} disabled={presenzeLoading}>
                  <Check size={13} /> {term ? 'Segna presenti i filtrati' : 'Segna tutti presenti'}
                </button>
                <button className="btn ghost sm" type="button" onClick={clearAllPresent} disabled={presenzeLoading}>
                  {term ? 'Azzera filtrati' : 'Azzera'}
                </button>
              </div>
            )}
          </div>
          <div className="card-body" style={{ paddingTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <span style={{ font: '700 22px var(--font-display)', color: 'var(--text-strong)' }}>
                {presentiCount}<span className="muted" style={{ font: '400 14px var(--font-body)' }}> / {totale} presenti</span>
              </span>
              <span className="mono-label" style={{ marginLeft: 'auto' }}>{pct}%</span>
            </div>
            <div className="progress" style={{ marginBottom: '16px' }}><i style={{ width: pct + '%' }} /></div>

            <div className="search" style={{ marginBottom: '14px' }}>
              <Search size={15} />
              <input
                type="text"
                value={presenzeSearch}
                onChange={e => setPresenzeSearch(e.target.value)}
                placeholder="Cerca per nome, codice, email…"
              />
            </div>

            {truncated && (
              <p className="muted" style={{ fontSize: '11.5px', marginBottom: '12px', color: 'var(--un-warning)' }}>
                Mostrati i primi {presenze.length} di {presenzeTotal} — usa la ricerca per trovare un invitato specifico
              </p>
            )}

            <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
              {presenzeLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--un-green-500)' }} />
                </div>
              ) : filtered.length === 0 ? (
                <div className="muted" style={{ textAlign: 'center', padding: '40px 0', fontSize: '12.5px' }}>
                  {presenze.length === 0 ? 'Nessun invitato: genera prima gli inviti dalla fase Invitati' : 'Nessun risultato per la ricerca'}
                </div>
              ) : (
                filtered.map(inv => {
                  const on = inv.checkedIn;
                  const nome = inv.recipientName || inv.associateId?.nominativo || '-';
                  const initials = nome.split(' ').slice(0, 2).map(x => x[0] || '').join('').toUpperCase() || '?';
                  return (
                    <div className={'person-row' + (on ? ' is-present' : '')} key={inv._id}>
                      <div className="pava">{initials}</div>
                      <div className="pinfo">
                        <div className="pn">{nome}</div>
                        <div className="pm">
                          <span className="code">{inv.associateId?.codice || (inv.recipientName ? 'per carica' : '')}</span>
                          {on && inv.checkedInAt && (
                            <span style={{ color: 'var(--un-green-700)' }}>Check-in: {formatDateTime(inv.checkedInAt)}</span>
                          )}
                        </div>
                      </div>
                      <button type="button" className="presence-toggle" aria-pressed={on} onClick={() => togglePresenza(inv)}>
                        {on ? <Check size={13} /> : <X size={13} />}
                        {on ? 'Presente' : 'Assente'}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </div>
    );
  };

  // -- Tab: Invitati --

  const renderInvitati = () => {
    const allegatiPieni = (event?.allegatiInvito?.length || 0) >= 5;

    return (
      <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Allegati all'invito (linkati nell'email) */}
        <section className="card">
          <div className="card-head">
            <Paperclip size={15} />
            <h2>Allegati all'invito</h2>
            <div className="aux">
              <label className={'btn soft sm' + (uploadingAllegato || allegatiPieni ? ' disabled-label' : '')}
                style={uploadingAllegato || allegatiPieni ? { opacity: 0.45, cursor: 'default' } : { cursor: 'pointer' }}>
                <Upload size={13} />
                {uploadingAllegato ? 'Caricamento…' : 'Aggiungi'}
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  className="hidden"
                  disabled={uploadingAllegato || allegatiPieni}
                  onChange={e => { handleUploadAllegatoInvito(e.target.files?.[0] || null); e.target.value = ''; }}
                />
              </label>
            </div>
          </div>
          <div className="card-body" style={{ paddingTop: '14px' }}>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '12.5px' }}>
              PDF e immagini (max 10 MB, fino a 5 file): compariranno come link scaricabili nell'email di invito.
            </p>
            {(event?.allegatiInvito?.length || 0) > 0 ? (
              event!.allegatiInvito!.map(a => (
                <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--un-ink-100)' }}>
                  <FileText size={14} />
                  <a
                    href={`${UPLOADS_BASE}/uploads/event-invites/${event!._id}/${encodeURIComponent(a.filename)}`}
                    target="_blank" rel="noreferrer"
                    style={{ font: '500 12.5px var(--font-body)', color: 'var(--un-green-700)', textDecoration: 'none' }}
                  >
                    {a.originalName}
                  </a>
                  <span className="muted" style={{ fontSize: '11px' }}>({Math.round(a.size / 1024)} KB)</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteAllegatoInvito(a._id)}
                    className="top-link exit"
                    style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: '12px' }}
                  >
                    Rimuovi
                  </button>
                </div>
              ))
            ) : (
              <div className="muted" style={{ fontSize: '12.5px' }}>Nessun allegato caricato.</div>
            )}
          </div>
        </section>

        {/* Gruppi rapidi */}
        <section className="card">
          <div className="card-head">
            <Users size={15} />
            <h2>Gruppi rapidi</h2>
          </div>
          <div className="card-body" style={{ paddingTop: '16px' }}>
            <div
              className="check-pill"
              role="checkbox"
              aria-checked={inviteTutti && tuttiStatoFilter === 'inregola' && tuttiProvincia === ''}
              onClick={() => (inviteTutti && tuttiStatoFilter === 'inregola' && tuttiProvincia === '') ? clearTutti() : enterTutti('inregola', '')}
              style={{ marginBottom: caricheOptions.length > 0 ? '14px' : '0' }}
            >
              <span className="box">{inviteTutti && tuttiStatoFilter === 'inregola' && tuttiProvincia === '' && <Check size={12} />}</span>
              <b>Tutti gli associati del tuo perimetro</b>
              <span className="muted">(in regola)</span>
            </div>
            {caricheOptions.length > 0 && (
              <>
                <div className="mono-label" style={{ marginBottom: '10px' }}>Per carica · titolari nel tuo territorio</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                  {caricheOptions.map(c => {
                    const checked = inviteCariche.includes(c._id);
                    return (
                      <div
                        key={c._id}
                        className="check-pill"
                        role="checkbox"
                        aria-checked={checked}
                        onClick={() => { setInviteCariche(prev => prev.includes(c._id) ? prev.filter(x => x !== c._id) : [...prev, c._id]); setInvitePreview(null); }}
                      >
                        <span className="box">{checked && <Check size={12} />}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {hasInviteSelection && (
              <div style={{ marginTop: '14px', padding: '12px 14px', background: 'var(--un-green-50)', borderLeft: '3px solid var(--un-green-500)', borderRadius: '0 var(--r-md) var(--r-md) 0' }}>
                {invitePreview ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ font: '600 13px var(--font-body)', color: 'var(--text-strong)' }}>
                        Inviterai {invitePreview.totale} persone
                      </div>
                      {invitePreview.totale > 0 && (
                        recipientList ? (
                          <button type="button" className="top-link" style={{ padding: '2px 8px', fontSize: '12px' }}
                            onClick={() => setRecipientList(null)}>
                            Nascondi elenco
                          </button>
                        ) : (
                          <button type="button" className="top-link" style={{ padding: '2px 8px', fontSize: '12px', color: 'var(--un-green-700)' }}
                            onClick={loadRecipientList} disabled={loadingRecipientList}>
                            {loadingRecipientList ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye size={13} />} Mostra elenco
                          </button>
                        )
                      )}
                    </div>
                    <div className="muted" style={{ fontSize: '11.5px', marginTop: '2px' }}>
                      {invitePreview.associati} associati + {invitePreview.rappresentanti} cariche
                      {invitePreview.duplicatiRimossi > 0 && ` · ${invitePreview.duplicatiRimossi} doppioni uniti`}
                      {invitePreview.senzaEmail > 0 && ` · ${invitePreview.senzaEmail} senza email saltati`}
                      {invitePreview.giaInvitati > 0 && ` · ${invitePreview.giaInvitati} già invitati`}
                    </div>

                    {recipientList && (
                      <div style={{ marginTop: '12px', maxHeight: '260px', overflowY: 'auto', background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', padding: '10px 12px' }}>
                        {recipientList.cariche.length > 0 && (
                          <>
                            <div className="mono-label" style={{ marginBottom: '6px' }}>Per carica ({recipientList.cariche.length})</div>
                            {recipientList.cariche.map((r, i) => (
                              <div key={`c${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '3px 0', fontSize: '12.5px' }}>
                                <span style={{ color: 'var(--text-strong)' }}>{r.nominativo || '—'}</span>
                                <span className="muted" style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email || 'senza email'}</span>
                              </div>
                            ))}
                          </>
                        )}
                        {recipientList.associati.length > 0 && (
                          <>
                            <div className="mono-label" style={{ margin: `${recipientList.cariche.length > 0 ? '10px' : '0'} 0 6px` }}>Associati ({recipientList.associati.length})</div>
                            {recipientList.associati.map((a, i) => (
                              <div key={`a${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '3px 0', fontSize: '12.5px' }}>
                                <span style={{ color: 'var(--text-strong)' }}>
                                  {a.nominativo}{a.codice ? <span className="muted" style={{ fontSize: '11px', marginLeft: '6px' }}>{a.codice}</span> : null}
                                </span>
                                <span className="muted" style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.email || 'senza email'}</span>
                              </div>
                            ))}
                          </>
                        )}
                        {recipientList.troncato && (
                          <div className="muted" style={{ fontSize: '11px', marginTop: '8px', fontStyle: 'italic' }}>
                            Elenco troncato ai primi 1000 per riga. Il totale reale è quello indicato sopra.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="muted" style={{ fontSize: '11.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {loadingInvitePreview && <Loader2 className="w-3 h-3 animate-spin" />}
                    Calcolo destinatari…
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Seleziona associati */}
        <section className="card">
          <div className="card-head">
            <UserPlus size={15} />
            <h2>Seleziona associati</h2>
            <div className="aux" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={selectAll}
                disabled={inviteTutti && tuttiStatoFilter === assocStatoFilter && tuttiProvincia === assocProvincia}
                className="top-link"
                style={{ padding: '4px 8px', color: 'var(--un-green-700)' }}
              >
                {(() => {
                  const parti = [
                    assocStatoFilter === 'inregola' ? 'in regola' : assocStatoFilter === 'nonregola' ? 'non in regola' : '',
                    assocProvincia || ''
                  ].filter(Boolean);
                  return parti.length ? `Seleziona tutti (${parti.join(' · ')})` : 'Seleziona tutti';
                })()}
              </button>
              <button
                type="button"
                onClick={deselectAll}
                disabled={!inviteTutti && selectedIds.size === 0}
                className="top-link"
                style={{ padding: '4px 8px' }}
              >
                Deseleziona
              </button>
            </div>
          </div>
          <div className="card-body" style={{ paddingTop: '14px' }}>
            <div className="toolbar" style={{ marginBottom: '10px' }}>
              <div className="search">
                <Search size={15} />
                <input
                  type="text"
                  value={assocSearch}
                  onChange={e => setAssocSearch(e.target.value)}
                  placeholder="Cerca per nome, codice, email…"
                />
              </div>
              <select
                className="select-pill"
                value={assocStatoFilter}
                onChange={e => { setAssocStatoFilter(e.target.value as '' | 'inregola' | 'nonregola'); setAssocPage(1); }}
                title="Filtra per stato tesseramento"
              >
                <option value="">Tutti i tesseramenti</option>
                <option value="inregola">In regola (pagato/attivato)</option>
                <option value="nonregola">Non in regola</option>
              </select>
              {provinceOptions.length > 1 && (
                <select
                  className="select-pill"
                  value={assocProvincia}
                  onChange={e => { setAssocProvincia(e.target.value); setAssocPage(1); }}
                  title="Filtra per provincia"
                >
                  <option value="">Tutte le province</option>
                  {provinceOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </div>

            {inviteTutti && (
              <div style={{ marginBottom: '10px', padding: '10px 12px', background: 'var(--un-green-50)', borderLeft: '3px solid var(--un-green-500)', borderRadius: '0 var(--r-md) var(--r-md) 0' }}>
                <span style={{ font: '600 12.5px var(--font-body)', color: 'var(--text-strong)' }}>
                  Stai invitando tutti gli associati del perimetro
                  {tuttiStatoFilter === 'inregola' ? ' in regola' : tuttiStatoFilter === 'nonregola' ? ' non in regola' : ''}
                  {tuttiProvincia ? ` della provincia di ${tuttiProvincia}` : ''}
                  {excludedIds.size > 0 ? ` · ${excludedIds.size} esclusi` : ''}.
                </span>
                <span className="muted" style={{ fontSize: '11.5px', display: 'block', marginTop: '2px' }}>
                  Tocca un associato per escluderlo, oppure «Deseleziona» per tornare alla scelta singola.
                </span>
              </div>
            )}

            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {assocForbidden ? (
                <div style={{ padding: '12px 0' }}>
                  <p style={{ font: '500 12.5px var(--font-body)', color: 'var(--un-warning)', margin: 0 }}>
                    Le tue cariche non ti permettono di invitare associati
                  </p>
                  <p className="muted" style={{ fontSize: '12px', marginTop: '4px' }}>{assocForbidden}</p>
                </div>
              ) : assocLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--un-green-500)' }} />
                </div>
              ) : associates.length === 0 ? (
                <div className="muted" style={{ textAlign: 'center', padding: '32px 0', fontSize: '12.5px' }}>Nessun associato trovato</div>
              ) : (
                associates.map(a => {
                  // in modalità "tutti": spuntate solo le righe che rientrano nel
                  // filtro stato dell'invito, salvo esclusioni manuali; le altre
                  // non sono incluse e non sono escludibili
                  const inTutti = inviteTutti && matchTuttiFilter(a);
                  const checked = a.isInvited
                    ? false
                    : (inviteTutti ? (inTutti && !excludedIds.has(a._id)) : selectedIds.has(a._id));
                  const onRowClick = a.isInvited
                    ? undefined
                    : (inviteTutti
                        ? (inTutti ? () => toggleExcluded(a._id) : undefined)
                        : () => toggleSelect(a._id));
                  return (
                    <div
                      className="person-row"
                      key={a._id}
                      style={{ cursor: onRowClick ? 'pointer' : 'default', opacity: a.isInvited ? 0.55 : (inviteTutti && !inTutti ? 0.5 : 1) }}
                      onClick={onRowClick}
                    >
                      <span
                        className="box"
                        style={{ width: '17px', height: '17px', borderRadius: '5px', flex: '0 0 auto', border: '1.5px solid var(--border-default)', display: 'grid', placeItems: 'center', color: '#fff', background: checked ? 'var(--un-green-500)' : 'transparent', borderColor: checked ? 'var(--un-green-500)' : undefined }}
                      >
                        {checked && <Check size={12} />}
                      </span>
                      <div className="pinfo">
                        <div className="pn">{a.nominativo}</div>
                        <div className="pm">
                          <span className="code">{a.codice}</span>
                          <span>{a.provincia}</span>
                          <span>{a.email}</span>
                        </div>
                      </div>
                      {a.isInvited && <span className="badge s-inviato">Già invitato</span>}
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => setAssocPage(Math.max(1, assocPage - 1))}
                disabled={assocPage <= 1}
                className="top-link"
                style={{ padding: '4px' }}
              >
                <ChevronLeft size={15} />
              </button>
              <span className="muted" style={{ fontSize: '12px' }}>{assocPage} / {assocTotalPages || 1}</span>
              <button
                type="button"
                onClick={() => setAssocPage(Math.min(assocTotalPages, assocPage + 1))}
                disabled={assocPage >= assocTotalPages}
                className="top-link"
                style={{ padding: '4px' }}
              >
                <ChevronRight size={15} />
              </button>
              <span className="muted" style={{ fontSize: '12px' }}>{assocTotal} totali</span>
              {hasInviteSelection && (
                <span className="muted" style={{ marginLeft: 'auto', fontSize: '12px', textAlign: 'right' }}>
                  Gli inviti per la selezione verranno generati e spediti con «{event.stato === 'BOZZA' ? 'Pubblica e invia' : 'Invia inviti'}»
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Azioni di fase: stesse CTA della testata, a fine moduli */}
        {event.isOwner && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {renderOwnerActions('bottom')}
          </div>
        )}
      </div>
    );
  };

  // -- Tab: Comunicazioni --

  const renderComunicazioni = () => {
    const totale = event.stats.total;
    const sent = event.stats.emailSent;
    const pct = totale ? Math.round((sent / totale) * 100) : 0;
    return (
      <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <section className="card">
          <div className="card-head">
            <Mail size={16} />
            <h2>Stato comunicazioni</h2>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ font: '700 32px var(--font-display)', letterSpacing: 'var(--tracking-tight)', color: 'var(--un-green-600)' }}>{sent}</span>
              <span className="muted" style={{ fontSize: '14px' }}>su {totale} inviti inviati</span>
              <span className="mono-label" style={{ marginLeft: 'auto' }}>{pct}%</span>
            </div>
            <div className="progress" style={{ marginTop: '12px' }}><i style={{ width: pct + '%' }} /></div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '14px', flexWrap: 'wrap' }}>
              <span className="muted" style={{ fontSize: '12.5px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--un-green-500)', display: 'inline-block' }} />
                Inviati {sent}
              </span>
              <span className="muted" style={{ fontSize: '12.5px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--un-warning)', display: 'inline-block' }} />
                In attesa di invio {pendingInvitations}
              </span>
            </div>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <Send size={16} />
            <h2>Azioni</h2>
          </div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 18px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', background: 'var(--un-ink-50)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ font: '600 13.5px var(--font-body)', color: 'var(--text-strong)' }}>Invia inviti</div>
                <div className="muted" style={{ fontSize: '12.5px', marginTop: '3px' }}>
                  Invia email, notifiche e push agli invitati che non li hanno ancora ricevuti.
                  {hasInviteSelection && ' Gli inviti per i destinatari appena selezionati verranno generati automaticamente.'}
                </div>
              </div>
              <button
                type="button"
                onClick={handleSendInvitations}
                disabled={sending || (pendingInvitations === 0 && !hasInviteSelection)}
                className="btn primary sm"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send size={13} />}
                Invia ({pendingInvitations + (hasInviteSelection ? (invitePreview?.totale ?? 0) : 0)})
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 18px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', background: 'var(--un-ink-50)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ font: '600 13.5px var(--font-body)', color: 'var(--text-strong)' }}>Invia promemoria</div>
                <div className="muted" style={{ fontSize: '12.5px', marginTop: '3px' }}>Invia un promemoria a tutti gli invitati.</div>
              </div>
              <button
                type="button"
                onClick={handleSendReminder}
                disabled={sending || event.stats.total === 0}
                className="btn warn sm"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell size={13} />}
                Promemoria
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // -- Fase: Verbale --
  // Autore: riepilogo con «Apri il verbale» (editor e assistente nella pagina
  // dedicata). Altri rappresentanti: lettura con allegati e «Scarica PDF».

  const formatDataLunga = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  const renderVerbale = () => {
    if (verbaleLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
        </div>
      );
    }

    if (verbaleIsOwner) {
      const nAllegati = verbaleAllegati.length;
      return (
        <div>
          {/* Promemoria: segnare le presenze prima di redigere il verbale */}
          {event.stato !== 'BOZZA' && (event.stats?.checkedIn ?? 0) === 0 && (
            <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-900/40 text-xs text-amber-800 dark:text-amber-300">
              ⚠️ Hai segnato le presenze? Il verbale e l'assistente AI usano l'elenco di presenti e assenti.
              <button
                onClick={() => setOpenPhase('presenze')}
                className="ml-2 underline font-medium text-amber-700 dark:text-amber-300"
              >
                Vai a Presenze
              </button>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Verbale della riunione</h4>
                  {verbaleBozza ? (
                    <span className="inline-flex px-2 py-0.5 text-2xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      Bozza
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-2xs font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      <CheckCircle className="w-3 h-3" />
                      {verbalePubblicatoAt ? `Pubblicato il ${formatDataLunga(verbalePubblicatoAt)}` : 'Pubblicato'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  {verbaleUltimaModifica
                    ? `Ultima modifica: ${formatDateTime(verbaleUltimaModifica)}`
                    : 'Non ancora scritto'}
                </p>
                <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  <Paperclip className="w-3 h-3 flex-shrink-0" />
                  {nAllegati === 0 ? 'Nessun allegato' : nAllegati === 1 ? '1 allegato' : `${nAllegati} allegati`}
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Link
                to={`/area-associativa/calendario/${id}/verbale`}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm transition-colors"
              >
                Apri il verbale
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      );
    }

    // Non autore: bozza (il backend non manda il testo) o verbale senza testo né allegati
    if (!verbaleLeggibile) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8">
          <div className="text-center">
            <FileText className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500">Il verbale non è ancora disponibile</p>
          </div>
        </div>
      );
    }

    // Sola lettura: il verbale formattato, gli allegati e il PDF ufficiale
    return (
      <div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-2xs text-gray-400 dark:text-gray-500">
              {verbaleUltimaModifica && `Verbale aggiornato il ${formatDataLunga(verbaleUltimaModifica)}`}
            </span>
            <button
              type="button"
              onClick={handleScaricaPdfVerbale}
              disabled={pdfVerbaleLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {pdfVerbaleLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Scarica PDF
            </button>
          </div>
          {verbaleConTesto ? (
            <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: verbaleContenutoSicuro }} />
          ) : (
            <p className="text-xs italic text-gray-500 dark:text-gray-400">Il verbale non contiene testo: vedi gli allegati.</p>
          )}
        </div>

        {/* Allegati */}
        <div className="mt-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Paperclip className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Allegati</h4>
            <span className="text-2xs text-gray-400 dark:text-gray-500">({verbaleAllegati.length})</span>
          </div>
          {verbaleAllegati.length > 0 ? (
            <div className="space-y-1.5">
              {verbaleAllegati.map(att => {
                // Link solo con il path valido (`/uploads/verbali/<eventId>/<file>`), come VerbaleLettura.
                const href = urlAllegato(att.path);
                return (
                  <div
                    key={att._id}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700"
                  >
                    <Paperclip className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-2xs text-gray-700 dark:text-gray-300 font-medium truncate flex-1">{att.filename}</span>
                    <span className="text-2xs text-gray-400 dark:text-gray-500 flex-shrink-0">{formatFileSize(att.size)}</span>
                    {href && (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors flex-shrink-0"
                        title="Scarica"
                      >
                        <Download className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-2xs text-gray-400 dark:text-gray-500 italic">Nessun allegato</p>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Link
            to={`/area-associativa/calendario/${id}/verbale`}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm transition-colors"
          >
            Leggi il verbale
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  };

  // -- Main render --

  // «Indietro» / «Avanti» in fondo a ogni fase: lo stesso ordine dello stepper.
  const renderNavigazioneFasi = () => {
    const fasi: [PhaseKey, string][] = event?.isOwner
      ? [['dati', 'Dati evento'], ['invitati', 'Invitati'], ['pubblica', 'Pubblica e invia'], ['presenze', 'Presenze'], ['verbale', 'Verbale']]
      : [['dati', 'Dettagli evento'], ['verbale', 'Verbale']];
    const i = fasi.findIndex(([k]) => k === openPhase);
    if (i < 0) return null;
    const prima = i > 0 ? fasi[i - 1] : null;
    const dopo = i < fasi.length - 1 ? fasi[i + 1] : null;
    const vai = (k: PhaseKey) => {
      setOpenPhase(k);
      contenutoFaseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    return (
      <nav aria-label="Fasi dell'evento" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
        {prima ? (
          <button type="button" className="btn ghost sm" onClick={() => vai(prima[0])}>
            <ArrowLeft size={14} /> Indietro: {prima[1]}
          </button>
        ) : <span />}
        {dopo && (
          <button type="button" className="btn primary sm" onClick={() => vai(dopo[0])}>
            Avanti: {dopo[1]} <ArrowRight size={14} />
          </button>
        )}
      </nav>
    );
  };

  // Contenuto della fase attualmente aperta (colonna destra dello stepper)
  const renderPhaseContent = () => {
    switch (openPhase) {
      case 'dati': return renderPanoramica();
      case 'invitati': return renderInvitati();
      case 'pubblica': return renderComunicazioni();
      case 'presenze': return renderPresenze();
      case 'verbale': return renderVerbale();
      default:
        return (
          <div className="muted" style={{ textAlign: 'center', padding: '48px 0', fontSize: '13px' }}>
            Seleziona una fase dallo stepper.
          </div>
        );
    }
  };

  // Badge stato evento (classe design system o colore inline per ANNULLATO)
  const renderStatoBadge = () => {
    const cls = statoBadgeClass[event.stato];
    const label = statoLabel[event.stato] || event.stato;
    return cls
      ? <span className={`badge ${cls}`}>{label}</span>
      : <span className="badge" style={{ background: '#FBEDEB', color: '#B23A2C' }}>{label}</span>;
  };

  return (
    <AssociativeAppShell
      route="calendario"
      nome={repProfile.nome}
      cognome={repProfile.cognome}
      caricaPrincipale={repProfile.caricaPrincipale}
      tipo={repProfile.tipo}
      foto={repProfile.foto}
    >
      <div className="page fade-in">
        {/* Back */}
        <button type="button" className="back-btn" onClick={() => navigate('/area-associativa/calendario')}>
          <ArrowLeft size={14} /> Calendario Attività
        </button>

        {/* Header: titolo + badge + CTA */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', margin: '10px 0 26px' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <h1 style={{ margin: 0, font: '700 25px/1.2 var(--font-display)', letterSpacing: 'var(--tracking-tight)', color: 'var(--text-strong)' }}>
              {event.nome}
            </h1>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
              {event.tipologiaId && (
                <span className="badge" style={{ background: `${event.tipologiaId.colore}20`, color: event.tipologiaId.colore }}>
                  <span className="dot" />{event.tipologiaId.nome}
                </span>
              )}
              {renderStatoBadge()}
              <span className="muted" style={{ fontSize: '12.5px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Calendar size={13} /> {formatDate(event.dataEvento)}
              </span>
            </div>
          </div>

          {/* CTA adattivo + menù Altro (solo organizzatore) */}
          {event.isOwner && renderOwnerActions('top')}
        </div>

        {/* Stepper + contenuto fase */}
        <div className="evento-grid">
          <div className="stepper card" style={{ padding: '22px 22px 8px' }}>
            {event.isOwner ? (
              <>
                <PhaseRow numero={1} titolo="Dati evento" descrizione="Titolo, data, luogo, allegati"
                  stato={phaseState('dati')} open={openPhase === 'dati'} onToggle={apriFase('dati')} />
                <PhaseRow numero={2} titolo="Invitati" descrizione={event.stats.total > 0 ? `${event.stats.total} invitati` : 'Gruppi rapidi e selezione'}
                  stato={phaseState('invitati')} open={openPhase === 'invitati'} onToggle={apriFase('invitati')} />
                <PhaseRow numero={3} titolo="Pubblica e invia" descrizione="Email, notifiche, promemoria"
                  stato={phaseState('pubblica')} open={openPhase === 'pubblica'} onToggle={apriFase('pubblica')} />
                <PhaseRow numero={4} titolo="Presenze" descrizione={event.stato === 'BOZZA' ? 'Dopo la pubblicazione' : 'Registro e check-in'}
                  stato={phaseState('presenze')} open={openPhase === 'presenze'} onToggle={apriFase('presenze')} />
                <PhaseRow numero={5} titolo="Verbale" descrizione="Redazione con assistente"
                  stato={phaseState('verbale')} open={openPhase === 'verbale'} onToggle={apriFase('verbale')} />
              </>
            ) : (
              <>
                <PhaseRow numero={1} titolo="Dettagli evento" descrizione="Informazioni e allegati"
                  stato="done" open={openPhase === 'dati'} onToggle={apriFase('dati')} />
                <PhaseRow numero={2} titolo="Verbale" descrizione="Resoconto della riunione"
                  stato={verbaleLeggibile ? 'done' : 'todo'} open={openPhase === 'verbale'} onToggle={apriFase('verbale')} />
              </>
            )}
          </div>

          <div ref={contenutoFaseRef} style={{ scrollMarginTop: '16px' }}>
            {renderPhaseContent()}
            {renderNavigazioneFasi()}
          </div>
        </div>

        {/* Confirm dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          type={confirmDialog.type as 'danger' | 'warning' | undefined}
        />
      </div>
    </AssociativeAppShell>
  );
};

export default AssociativeEventDetail;
